import EpdRoutes from 'routes/EpdRoutes';
import { generateExpireTime } from 'util/utils';
import queryClient from 'util/queryClient';
import {
  ChangePasswordModel,
  ConfirmEmailModel,
  ForgotPasswordModel,
  LastLoginCompanyModel,
  LoginModel,
  RegisterModel,
  ResetPasswordModel,
  SecurityClient,
  UpdateUserEmailModel,
  UpdateUserNameModel,
  UserModel,
} from './EpdClient';
import { invalidateCurrentUser } from 'hooks/useCurrentUser';
import { RoleName } from 'constants/constants';

const AUTH_TOKEN = 'auth_expires';
const LATEST_LOGIN_COMPANY_ID = 'latest_login_company_id';

const client = new SecurityClient(process.env.REACT_APP_API_URL);

interface Token {
  expires: number;
  user: UserModel;
}

const userReloader = createUserModelReloader();
userReloader.syncUserWithDatabase(); // Reload user on each page load (yes, outside of React tree because this way the server has more time to respond)

class AuthService {
  static isAdmin = () => {
    const token = getToken();
    return token?.user?.roles?.some((x) => x.name === RoleName.ADMIN);
  };

  static isSystemAdmin = () => {
    const token = getToken();
    return token?.user?.roles?.some((x) => x.name === RoleName.SYSTEM_ADMIN);
  };

  static isCompanyOwner = (companyId: string): boolean => {
    const token = getToken();

    return token?.user?.memberships?.some((x) => x.companyId === companyId && x.roleName === RoleName.EPD_OWNER) || false;
  };

  static hasOnlyLcaPractitionerCompanyRole = (companyId: string): boolean => {
    const token = getToken();
    const userRoles = token?.user?.roles?.map((r) => r.name) || [];
    const memberships = token?.user?.memberships?.filter((x) => x.companyId === companyId) || [];
    return (
      !userRoles.some((role) => role === RoleName.ADMIN || role === RoleName.LICENSEE_ADMIN) &&
      memberships.length === 1 &&
      memberships[0].roleName === RoleName.LCA_PRACTITIONER
    );
  };

  static getCompanyRoles = (companyId: string): RoleName[] => {
    const token = getToken();
    const allMemberships = token?.user?.memberships;
    if (!allMemberships) {
      throw new Error('Local storage is missing memberships.');
    }
    return allMemberships.filter((m) => m.companyId === companyId).map((m) => m.roleName);
  };

  static isLicenseeAdmin = () => {
    const token = getToken();

    return token?.user?.roles?.some((x) => x.name === RoleName.LICENSEE_ADMIN);
  };

  static isAdminLicenseeInCompany = async (companyId: string): Promise<boolean> => {
    const companiesId = await client.getCompaniesForLicenseeAdmin();
    return companiesId.some((x) => x === companyId);
  };

  static isAuthenticated = () => {
    const token = getToken();

    if (!token || !token?.expires || token.expires < new Date().getTime()) {
      AuthService.removeToken();
      return false;
    }

    return true;
  };

  static login = async (values: LoginModel) => {
    queryClient.clear();

    const user = await client.login(values);

    const token: Token = {
      expires: generateExpireTime(),
      user: user,
    };

    AuthService.setToken(token);

    return user;
  };

  static loginAs = async (values: LoginModel) => {
    const user = await client.loginAs(values);

    const token: Token = {
      expires: generateExpireTime(),
      user: user,
    };

    AuthService.setToken(token);

    return user;
  };

  static setToken = (token: Token) => {
    setToken(token);
    AuthService.setUserLatestLoginCompany(token.user.latestLoginCompanyId);
  };

  static setUserLatestLoginCompany = (companyId: string | null) => {
    if (companyId) localStorage.setItem(LATEST_LOGIN_COMPANY_ID, companyId);
  };

  static getUser = () => {
    const token = getToken();
    return token?.user;
  };

  static getUserLatestLoginCompany = () => {
    const latestLoginCompanyId = localStorage.getItem(LATEST_LOGIN_COMPANY_ID);

    return latestLoginCompanyId;
  };

  static isLoggedin = () => {
    return !!getToken();
  };

  static removeToken = () => {
    clearToken();
  };

  static logout = async (history: any) => {
    try {
      await client.logout();
    } catch (error) {
    } finally {
      AuthService.removeToken();
      history.push(EpdRoutes.login);
    }
  };

  static updateUserLatestLoginCompanyId = async (model: LastLoginCompanyModel) => {
    if (this.isLoggedin()) {
      await client.updateUserLatestLoginCompanyId(model);
      AuthService.setUserLatestLoginCompany(model.companyId);
    }
  };

  static acceptNewTermsAndConditions = async (newTermsAndConditionsAccepted: boolean) => {
    const acceptedDate = await client.acceptNewTermsAndConditions(newTermsAndConditionsAccepted);

    const storageItem = localStorage.getItem(AUTH_TOKEN);
    const token: Token = storageItem && JSON.parse(storageItem);

    const tokenWithUpdatedUserAcceptedTermsData = {
      ...token,
      user: { ...token?.user, acceptsNewTermsOfConditionsDate: acceptedDate },
    } as Token;

    AuthService.setToken(tokenWithUpdatedUserAcceptedTermsData);
  };

  static confirmEmail = async (values: ConfirmEmailModel) => {
    await client.confirmEmail(values);
  };

  static resetPassword = async (values: ResetPasswordModel) => {
    await client.resetPassword(values);
  };

  static validateResetPasswordToken = client.validateResetPasswordToken;

  static forgotPassword = async (values: ForgotPasswordModel) => {
    await client.forgotPassword(values);
  };

  static registerUser = async (values: RegisterModel, theme: string) => {
    await client.register(values, theme);
  };

  static changePassword = async (values: ChangePasswordModel) => {
    await client.changePassword(values);
  };

  static async updateUserName(values: UpdateUserNameModel) {
    await client.updateUserName(values);

    const storageItem = localStorage.getItem(AUTH_TOKEN);
    if (!storageItem) {
      throw new Error('Local storage is missing auth token.');
    }

    const oldToken = JSON.parse(storageItem) as Token;
    AuthService.setToken({
      ...oldToken,
      user: {
        ...oldToken.user,
        ...values,
      },
    });
  }

  static validateEmail = async (email: string) => {
    await client.validateEmail(email);
  };

  static sendEmailUpdateVerificationCode = async (newEmailAddress: string) => {
    await client.sendEmailUpdateVerificationCode(newEmailAddress);
  };

  static validateEmailUpdateVerificationCode = async (verificationCode: string) => {
    return await client.validateEmailUpdateVerificationCode(verificationCode);
  };

  static updateUserEmail = async (values: UpdateUserEmailModel) => {
    await client.updateUserEmail(values);
  };

  static refreshUser = async () => {
    await userReloader.syncUserWithDatabase();
  };
}

export default AuthService;

function getToken(): Token | null {
  const storageItem = localStorage.getItem(AUTH_TOKEN);
  const token = storageItem ? (JSON.parse(storageItem) as Token) : null;
  return token;
}

function setToken(token: Token): void {
  localStorage.setItem(AUTH_TOKEN, JSON.stringify(token));
  invalidateCurrentUser();
}

function clearToken(): void {
  localStorage.removeItem(AUTH_TOKEN);
  invalidateCurrentUser();
}

function createUserModelReloader() {
  let currentOperation: {
    promise: Promise<UserModel>;
    abortController: AbortController;
  } | null = null;

  async function syncUserWithDatabase() {
    if (currentOperation) {
      currentOperation.abortController.abort();
      currentOperation = null;
    }

    const token = getToken();
    if (!token) {
      return;
    }

    const abortController = new AbortController();
    currentOperation = {
      promise: client.getMe({ signal: abortController.signal }),
      abortController,
    };

    try {
      const user = await currentOperation.promise;
      setToken({ ...token, user });
    } catch (e) {
      console.error('Error loading current user', e);
    } finally {
      currentOperation = null;
    }
  }

  return { syncUserWithDatabase };
}
